import type React from "react";
import { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { hasLicense, LICENSE_KEY } from "../../../constants/licenses";
import { AuthContext } from "../../../hooks/authProvider/authProvider";
import { useEmailCompose } from "../../../hooks/use-email-compose";
import type { MatchingResultItem } from "../../../hooks/use-free-matching-api";
import { useFreeMatchingApi } from "../../../hooks/use-free-matching-api";
import { useMatchingConditions } from "../../../hooks/use-matching-conditions-api";
import { useProposalComposer } from "../../../hooks/use-proposal-composer";
import { useEmailDetailRestApi } from "../../../hooks/useEmailDetailRestApi";
import { RecruitmentType } from "../../../hooks/useEmailsRestApi";
import { getCurrentTenant } from "../../../utils/authority-utils";
import { toastError } from "../../../utils/toast-utils";
import { EmailDetailPage, type RightPanelMode } from "./EmailDetailPage";

export const EmailDetailPageContainer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [freeWords, setFreeWords] = useState<string[]>([]);
  const [rightPanelMode, setRightPanelMode] =
    useState<RightPanelMode>("results");
  const { currentUser } = useContext(AuthContext);

  // REST API を使用してメール詳細を取得
  const { data, loading, error } = useEmailDetailRestApi({
    variables: { id: id! },
    skip: !id,
  });

  // マッチングライセンスチェック
  const isMatchingEnabled = hasLicense(
    getCurrentTenant(currentUser)?.licenses,
    LICENSE_KEY.MATCHING,
  );

  // マッチング API hook
  const {
    executeMatching,
    fetchExistingResults,
    updateResultStatus,
    upsertFeedback,
    clearResult,
    loading: matchingLoading,
    error: matchingError,
    jobResult,
  } = useFreeMatchingApi();

  // マッチング設定の状態を取得
  const {
    settings: matchingConditionsList,
    loading: matchingConditionsLoading,
  } = useMatchingConditions();
  const hasActiveMatchingCondition =
    !matchingConditionsLoading && matchingConditionsList.some(s => s.isActive);

  // 提案メール hook
  const proposal = useProposalComposer();

  // 返信メール hook
  const reply = useEmailCompose();

  // メール変更時にマッチング結果をリセット
  // biome-ignore lint/correctness/useExhaustiveDependencies: id is intentionally used to trigger reset on email change
  useEffect(() => {
    clearResult();
    proposal.resetDraft();
    reply.resetDraft();
    setRightPanelMode("results");
  }, [id, clearResult, proposal.resetDraft, reply.resetDraft]);

  // メールデータ取得後に既存マッチング結果を読み込む
  useEffect(() => {
    if (data?.email?.id && isMatchingEnabled) {
      fetchExistingResults(data.email.id);
    }
  }, [data?.email?.id, isMatchingEnabled, fetchExistingResults]);

  // URLのクエリパラメータからfreeWordsを取得
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const freeWordsParam = urlParams.get("freeWords");
    if (freeWordsParam) {
      setFreeWords(freeWordsParam.split(",").filter(word => word.trim()));
    }
  }, []);

  const handleExecuteMatching = useCallback(async () => {
    const email = data?.email;
    if (!email) return;

    const sourceType =
      email.recruitmentType === RecruitmentType.HumanResource ? "hr" : "matter";

    try {
      const result = await executeMatching(email.id, sourceType);
      if (result.resultsSaved > 0) {
        toast.success(
          `${result.candidatesSearched}件から${result.resultsSaved}件のマッチを発見しました`,
        );
      } else {
        toast.info("マッチする対向メールが見つかりませんでした");
      }
    } catch (e) {
      if (
        e instanceof Error &&
        "status" in e &&
        (e as { status: number }).status === 429
      ) {
        toastError("マッチングの利用上限に達しました。");
      } else {
        toastError("マッチングの実行に失敗しました。");
      }
    }
  }, [data?.email, executeMatching]);

  const handlePropose = useCallback(
    (result: MatchingResultItem) => {
      const email = data?.email;
      if (!email) return;
      proposal.startProposal(result, email);
      setRightPanelMode("compose");
    },
    [data?.email, proposal.startProposal],
  );

  const handleReply = useCallback(() => {
    const email = data?.email;
    if (!email) return;
    reply.startReply(email);
    setRightPanelMode("reply");
  }, [data?.email, reply.startReply]);

  const handleBackToResults = useCallback(() => {
    setRightPanelMode("results");
    reply.resetDraft();
  }, [reply.resetDraft]);

  // 詳細比較
  const [comparisonTarget, setComparisonTarget] =
    useState<MatchingResultItem | null>(null);

  const handleCompare = useCallback((result: MatchingResultItem) => {
    setComparisonTarget(result);
  }, []);

  const handleCloseComparison = useCallback(() => {
    setComparisonTarget(null);
  }, []);

  const handleSendReply = useCallback(async (): Promise<boolean> => {
    const success = await reply.sendEmail();
    if (success) {
      toast.success("返信メールを送信しました");
      setRightPanelMode("results");
      reply.resetDraft();
    } else {
      toastError("メールの送信に失敗しました");
    }
    return success;
  }, [reply.sendEmail, reply.resetDraft]);

  const handleSendProposal = useCallback(async (): Promise<boolean> => {
    const success = await proposal.sendProposal();
    if (success) {
      toast.success("提案メールを送信しました");
      // ステータスを更新
      if (proposal.draft) {
        try {
          await updateResultStatus(proposal.draft.resultId, "proposed");
        } catch {
          // ステータス更新失敗は致命的ではない
        }
      }
      setRightPanelMode("results");
      proposal.resetDraft();
    } else {
      toastError("メールの送信に失敗しました");
    }
    return success;
  }, [
    proposal.sendProposal,
    proposal.draft,
    proposal.resetDraft,
    updateResultStatus,
  ]);

  if (error) {
    return (
      <div className="flex justify-center mt-4">
        <div className="text-left text-red-500 max-w-xl">
          <p>メールの取得でエラーが発生しました。</p>
        </div>
      </div>
    );
  }

  return (
    <EmailDetailPage
      isLoading={loading}
      email={data?.email || null}
      freeWords={freeWords}
      isMatchingEnabled={isMatchingEnabled}
      hasActiveMatchingCondition={hasActiveMatchingCondition}
      isMatchingLoading={matchingLoading}
      matchingError={matchingError}
      matchingJob={jobResult}
      onExecuteMatching={handleExecuteMatching}
      onDismissMatchingError={clearResult}
      rightPanelMode={rightPanelMode}
      proposalDraft={proposal.draft}
      isProposalGenerating={proposal.isGenerating}
      isProposalSending={proposal.isSending}
      proposalError={proposal.error}
      onPropose={handlePropose}
      onProposalFieldChange={proposal.setField}
      onSendProposal={handleSendProposal}
      onBackToResults={handleBackToResults}
      onCancelGeneration={proposal.cancelGeneration}
      // Reply
      replyDraft={reply.draft}
      isReplyGenerating={reply.isGenerating}
      isReplySending={reply.isSending}
      replyError={reply.error}
      onReply={handleReply}
      onReplyFieldChange={reply.setField}
      onAddRecipient={reply.addRecipient}
      onRemoveRecipient={reply.removeRecipient}
      onGenerateReplyBody={reply.generateBody}
      onSendReply={handleSendReply}
      onCancelReplyGeneration={reply.cancelGeneration}
      // Comparison
      comparisonTarget={comparisonTarget}
      onCompare={handleCompare}
      onCloseComparison={handleCloseComparison}
      onFeedback={upsertFeedback}
    />
  );
};
